import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Tooltip from 'components/shared/Tooltip';
import { capitalize } from 'lib/util';
import LocationEpisode from 'models/LocationEpisode';
import { ReviewStatus } from 'models/Review';
import colors from 'styles/theme/colors';
import DischargingToIcon from 'svg/DischargingToIcon';
import LocationPinIcon from 'svg/LocationPinIcon';

export type PayerType = 'medicare' | 'other';

interface DischargeCalendarPatientCardProps {
  entry: LocationEpisode;
  payerType: PayerType;
  height?: number;
  showGroupName?: boolean;
}

function PatientTooltip({ entry }: { entry: LocationEpisode }) {
  const latestReferral = entry.latestReferralReview;
  const referralStatusColor = (() => {
    switch (latestReferral?.status) {
      case ReviewStatus.ACCEPTED:
        return colors.accentGreen;
      case ReviewStatus.REJECTED:
        return colors.accentRed;
      default:
        return colors.white;
    }
  })();

  const planningDischargeTo = entry.projectedDischargeReview?.planningDischargeTo;
  const referral = entry.latestReferralReview;
  const referralGroup = referral?.group;

  return (
    <TooltipContainer>
      <TooltipPatientName>{entry.patient.name}</TooltipPatientName>
      <ul>
        {planningDischargeTo && (
          <li>
            <TooltipSectionLabel>Discharging To:</TooltipSectionLabel>{' '}
            <TooltipSectionValue>{planningDischargeTo}</TooltipSectionValue>
          </li>
        )}
        {referralGroup && (
          <>
            <li>
              <TooltipSectionLabel>{referralGroup.groupType.displayName}:</TooltipSectionLabel>{' '}
              <TooltipSectionValue>{referralGroup.name}</TooltipSectionValue>
            </li>
            <li>
              <TooltipSectionLabel>Referral Status:</TooltipSectionLabel>{' '}
              <TooltipSectionValue style={{ color: referralStatusColor }}>
                {capitalize(referral.statusLabel)}
              </TooltipSectionValue>
            </li>
          </>
        )}
      </ul>
    </TooltipContainer>
  );
}

export default function DischargeCalendarPatientCard({
  entry,
  payerType,
  height,
  showGroupName,
}: DischargeCalendarPatientCardProps) {
  const navigate = useNavigate();

  const patientName = entry.patient.name ?? '';
  const isDischarged = entry.discharged;
  const hasTooltipData = !!entry.projectedDischargeReview?.planningDischargeTo || !!entry.latestReferralReview?.group;

  const handleClick = () => {
    navigate(`/patients/${entry.episodeId}`);
  };

  const card = (
    <Card
      $payerType={payerType}
      $isDischarged={isDischarged}
      $height={height}
      onClick={handleClick}
      data-cy={`discharge-calendar-patient-card-${entry.id}`}>
      <PatientName>
        {patientName} ({entry.lengthOfStay})
      </PatientName>
      {showGroupName && entry.group?.name && (
        <IconRow>
          <LocationPinIcon width={8} height={8} />
          <GroupName>{entry.group.name}</GroupName>
        </IconRow>
      )}
      <IconRow>
        <DischargingToIcon width={8} height={8} />
        <PlanningDischargeTo>{entry.projectedDischargeReview?.planningDischargeTo || '—'}</PlanningDischargeTo>
      </IconRow>
    </Card>
  );

  if (hasTooltipData) {
    return (
      <Tooltip position='bottom' text={<PatientTooltip entry={entry} />}>
        {card}
      </Tooltip>
    );
  }

  return card;
}

const Card = styled.div<{ $payerType: PayerType; $isDischarged: boolean; $height?: number }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 10px;
  border-radius: 2px;
  cursor: pointer;
  transition: opacity 0.2s ease;
  opacity: ${({ $isDischarged }) => ($isDischarged ? 0.25 : 1)};

  background-color: ${({ $payerType, theme }) =>
    $payerType === 'medicare' ? theme.colors.accentGreen10 : 'rgba(64, 159, 242, 0.1)'};

  border-left: 3px solid
    ${({ $payerType, theme }) => ($payerType === 'medicare' ? theme.colors.accentGreen : theme.colors.pictonBlue)};
  height: ${({ $height }) => ($height ? `${$height}px` : 'auto')};
  flex-shrink: 0;

  &:hover {
    opacity: ${({ $isDischarged }) => ($isDischarged ? 0.35 : 0.9)};
  }
`;

const PatientName = styled.span`
  font-size: 12px;
  font-weight: var(--font-weight-medium);
  color: ${({ theme }) => theme.colors.black};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
`;

const IconRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;

  svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.black75};
  }
`;

const GroupName = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.black75};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PlanningDischargeTo = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.black75};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TooltipContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 6px;

  ul {
    margin-top: 6px;
    padding-inline-start: 13px;
    li {
      margin: 6px 0;
      text-indent: -2px;
    }
  }
`;

const TooltipPatientName = styled.span`
  font-size: 12px;
  font-weight: 700;
`;

const TooltipSectionLabel = styled.span`
  font-size: 10px;
`;

const TooltipSectionValue = styled.span`
  font-size: 10px;
  font-weight: 700;
`;
